import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft as ArrowLeftIcon, Printer as PrinterIcon } from 'lucide-react';
import pacientesService from '../../services/pacientesService';
import medicoVfpService, { PacienteVfp, DoctorInfo, ClasificacionValor } from '../../services/medicoVfpService';
import {
  DEFAULT_FIELD_POSITIONS,
  FieldPosition,
  RECIPE_FIELD_KEYS,
  RecipeFieldKey,
} from '../../components/receta/RecipePrintCanvas';
import RecipeBeneficenciaCanvas, {
  RecipeBeneficenciaFieldKey,
  DEFAULT_BENEFICENCIA_POSITIONS,
  RECIPE_BENEFICENCIA_FIELD_KEYS,
} from '../../components/receta/RecipeBeneficenciaCanvas';
import RecipeAngelesCanvas, {
  RecipeAngelesFieldKey,
  DEFAULT_ANGELES_POSITIONS,
  RECIPE_ANGELES_FIELD_KEYS,
} from '../../components/receta/RecipeAngelesCanvas';

type RecipeFormat = 'beneficencia' | 'angeles';
type AnyRecipeFieldKey = RecipeFieldKey | RecipeBeneficenciaFieldKey | RecipeAngelesFieldKey;

function formatMexicoCityDate(now: Date) {
  const tz = 'America/Mexico_City';
  const parts = new Intl.DateTimeFormat('es-MX', {
    timeZone: tz,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const month = get('month').replace('.', '').toUpperCase();
  return `${get('day')} ${month} ${get('year')}`;
}

function calcularEdadDetallada(nacimiento?: Date | string | null) {
  if (!nacimiento) return { anios: 0, meses: 0, dias: 0 };
  const birth = new Date(nacimiento);
  if (Number.isNaN(birth.getTime())) return { anios: 0, meses: 0, dias: 0 };

  const today = new Date();
  let y = today.getFullYear() - birth.getFullYear();
  let m = today.getMonth() - birth.getMonth();
  let d = today.getDate() - birth.getDate();

  if (d < 0) {
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    d += prevMonth.getDate();
    m -= 1;
  }
  if (m < 0) {
    m += 12;
    y -= 1;
  }
  return { anios: Math.max(0, y), meses: Math.max(0, m), dias: Math.max(0, d) };
}

export default function MedicoVfpRecetaPrintPage() {
  const navigate = useNavigate();
  const { state } = useLocation() as {
    state?: {
      prescription?: string;
      date?: string;
      ageYears?: string;
      ageMonths?: string;
      ageDays?: string;
      weight?: string;
      height?: string;
      temperature?: string;
      weightPercentile?: string;
      heightPercentile?: string;
      recetaToken?: string | null;
      formato?: string;
    };
  };
  const { clave } = useParams<{ clave: string }>();
  const [loading, setLoading] = useState(true);
  const [paciente, setPaciente] = useState<PacienteVfp | null>(null);
  const [doctorInfo, setDoctorInfo] = useState<DoctorInfo | null>(null);
  const [recipeFormat, setRecipeFormat] = useState<RecipeFormat>(() => {
    if (state?.formato === 'beneficencia' || state?.formato === 'angeles') return state.formato;
    const saved = localStorage.getItem('recipeFormat');
    return (saved === 'beneficencia' || saved === 'angeles') ? saved : 'beneficencia';
  });
  const [weightPercentile, setWeightPercentile] = useState<string>('');
  const [heightPercentile, setHeightPercentile] = useState<string>('');

  // Layout state (reutiliza mismo backend que Receta de pacientes)
  const [editMode, setEditMode] = useState(false);
  const [selectedField, setSelectedField] = useState<AnyRecipeFieldKey | null>(null);
  const [fieldPositions, setFieldPositions] = useState<Partial<Record<string, FieldPosition>>>({});
  const [fieldDimensions, setFieldDimensions] = useState<
    Partial<Record<string, { width: number; height: number }>>
  >({});
  const [initialLayout, setInitialLayout] = useState<Record<string, FieldPosition>>(
    RECIPE_FIELD_KEYS.reduce((acc, k) => ({ ...acc, [k]: { ...DEFAULT_FIELD_POSITIONS[k] } }), {} as Record<string, FieldPosition>)
  );

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        if (!clave) return;
        
        // Cargar paciente y doctor en paralelo
        const [pacienteData, doctor] = await Promise.all([
          medicoVfpService.getPacienteVfp(Number(clave)),
          medicoVfpService.getDoctorInfo().catch(() => null),
        ]);
        
        setPaciente(pacienteData);
        setDoctorInfo(doctor);
        
        // Cargar percentiles si hay datos disponibles
        if (pacienteData) {
          try {
            const pesoTallaData = await medicoVfpService.getPacientePesoTalla(Number(clave));
            
            // Formatear percentiles de peso
            if (pesoTallaData.comparacion) {
              const pesoClass = pesoTallaData.comparacion.peso as ClasificacionValor;
              
              if (pesoClass && pesoClass !== 'fuera_de_rango') {
                let pesoLabel = '';
                switch (pesoClass) {
                  case 'muy_bajo':
                    pesoLabel = 'Muy bajo (< P3)';
                    break;
                  case 'bajo':
                    pesoLabel = 'Bajo (P3-P15)';
                    break;
                  case 'normal_bajo':
                    pesoLabel = 'Normal bajo (P15-P50)';
                    break;
                  case 'normal':
                    pesoLabel = 'Normal (P50-P85)';
                    break;
                  case 'alto':
                    pesoLabel = 'Alto (P85-P97)';
                    break;
                  case 'muy_alto':
                    pesoLabel = 'Muy alto (> P97)';
                    break;
                }
                if (pesoLabel) {
                  setWeightPercentile(pesoLabel);
                }
              }
              
              // Formatear percentiles de talla
              const tallaClass = pesoTallaData.comparacion.talla as ClasificacionValor;
              
              if (tallaClass && tallaClass !== 'fuera_de_rango') {
                let tallaLabel = '';
                switch (tallaClass) {
                  case 'muy_bajo':
                    tallaLabel = 'Muy bajo (< P3)';
                    break;
                  case 'bajo':
                    tallaLabel = 'Bajo (P3-P15)';
                    break;
                  case 'normal_bajo':
                    tallaLabel = 'Normal bajo (P15-P50)';
                    break;
                  case 'normal':
                    tallaLabel = 'Normal (P50-P85)';
                    break;
                  case 'alto':
                    tallaLabel = 'Alto (P85-P97)';
                    break;
                  case 'muy_alto':
                    tallaLabel = 'Muy alto (> P97)';
                    break;
                }
                if (tallaLabel) {
                  setHeightPercentile(tallaLabel);
                }
              }
            }
          } catch (error) {
            console.error('Error al cargar los percentiles:', error);
          }
        }
      } catch (error) {
        console.error('Error al cargar paciente VFP:', error);
        toast.error('Error al cargar paciente');
        navigate('/medico-vfp/pacientes');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [clave, navigate]);

  // Cargar/guardar layout
  useEffect(() => {
    const loadLayout = async () => {
      try {
        const layouts = await pacientesService.getRecetaLayout(recipeFormat);
        
        // Cargar posiciones para ambos formatos
        const layoutMap = layouts.reduce<Partial<Record<string, FieldPosition>>>((acc, layout) => {
          acc[layout.campo] = {
            top: layout.top,
            left: layout.left,
            ...(layout.width != null ? { width: layout.width } : {}),
          };
          return acc;
        }, {});

        // Combinar con defaults (usar el formato actual)
        const allKeys = recipeFormat === 'beneficencia' 
          ? RECIPE_BENEFICENCIA_FIELD_KEYS 
          : recipeFormat === 'angeles'
          ? RECIPE_ANGELES_FIELD_KEYS
          : RECIPE_FIELD_KEYS;
        const defaultPositions: Record<string, FieldPosition> = recipeFormat === 'beneficencia'
          ? DEFAULT_BENEFICENCIA_POSITIONS
          : recipeFormat === 'angeles'
          ? DEFAULT_ANGELES_POSITIONS
          : DEFAULT_FIELD_POSITIONS;

        const merged = allKeys.reduce<Record<string, FieldPosition>>((acc, key) => {
          const base = defaultPositions[key];
          const override = layoutMap[key];
          acc[key] = { ...base, ...(override ?? {}) };
          return acc;
        }, {});

        // Si qrCode es campo nuevo sin posición guardada, alinearlo debajo del doctor
        if (!layoutMap['qrCode'] && merged['qrCode']) {
          if (recipeFormat === 'angeles') {
            const anchor = merged['doctorLine3'] || merged['doctorLine1'];
            if (anchor) {
              merged['qrCode'] = { top: anchor.top + 80, left: anchor.left };
            }
          } else {
            const anchor = merged['doctorName'];
            if (anchor) {
              merged['qrCode'] = { top: anchor.top + 80, left: 1300 };
            }
          }
        }

        const overrides = allKeys.reduce<Partial<Record<string, FieldPosition>>>((acc, key) => {
          const base = defaultPositions[key];
          const next = merged[key];
          if (next.top !== base.top || next.left !== base.left || (next.width ?? null) !== (base.width ?? null)) {
            acc[key] = { ...next };
          }
          return acc;
        }, {});

        setInitialLayout(merged);
        setFieldPositions(overrides);
      } catch (e) {
        console.error('Error al cargar layout de receta:', e);
      }
    };
    loadLayout();
  }, [recipeFormat]);

  const now = useMemo(() => new Date(), []);
  const fechaMx = useMemo(() => formatMexicoCityDate(now), [now]);
  const edad = useMemo(() => calcularEdadDetallada(paciente?.nacimiento ?? null), [paciente]);

  const PX_TO_CANVAS_UNITS = 2.64583333;
  const BASE_KEY_STEP = Math.max(1, Math.round(PX_TO_CANVAS_UNITS));

  const handleFieldBoundsChange = useCallback(
    (field: AnyRecipeFieldKey, size: { width: number; height: number }) => {
      const widthUnits = Math.max(0, Math.round(size.width * PX_TO_CANVAS_UNITS));
      const heightUnits = Math.max(0, Math.round(size.height * PX_TO_CANVAS_UNITS));
      setFieldDimensions((prev) => {
        const existing = prev[field];
        if (existing && existing.width === widthUnits && existing.height === heightUnits) return prev;
        return { ...prev, [field]: { width: widthUnits, height: heightUnits } };
      });
    },
    [PX_TO_CANVAS_UNITS],
  );

  const updateFieldPosition = (field: string, updater: (current: FieldPosition) => FieldPosition) => {
    setFieldPositions((prev) => {
      // Usar las posiciones correctas según el formato
      const defaultPositions: Record<string, FieldPosition> = recipeFormat === 'beneficencia' 
        ? DEFAULT_BENEFICENCIA_POSITIONS
        : recipeFormat === 'angeles'
        ? DEFAULT_ANGELES_POSITIONS
        : DEFAULT_FIELD_POSITIONS;
      
      const base = defaultPositions[field];
      if (!base) {
        console.warn(`No se encontró posición base para el campo: ${field}`);
        return prev;
      }
      
      const current = { ...base, ...prev[field] };
      const updated = updater(current);
      const sanitized: FieldPosition = { top: Math.round(updated.top), left: Math.round(updated.left) };
      const sameAsDefault = sanitized.top === base.top && sanitized.left === base.left;
      if (sameAsDefault) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: sanitized };
    });
  };

  useEffect(() => {
    if (!editMode) return;
    const CANVAS_WIDTH = 1800;   // 18cm = 1800 unidades (ampliado para más espacio de edición)
    const CANVAS_HEIGHT = 3071;  // 2670 + 15% = 3070.5 ≈ 3071
    const handler = (event: KeyboardEvent) => {
      if (!selectedField) return;
      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (!arrowKeys.includes(event.key)) return;
      event.preventDefault();
      const multiplier = event.shiftKey ? 5 : 1;
      const delta = BASE_KEY_STEP * multiplier;
      updateFieldPosition(selectedField, (current) => {
        const dimension = fieldDimensions[selectedField];
        const defaultPositions: Record<string, FieldPosition> = recipeFormat === 'beneficencia' 
          ? DEFAULT_BENEFICENCIA_POSITIONS
          : recipeFormat === 'angeles'
          ? DEFAULT_ANGELES_POSITIONS
          : DEFAULT_FIELD_POSITIONS;
        const fallbackWidth = current.width ?? defaultPositions[selectedField as string]?.width ?? 0;
        const width = dimension?.width ?? fallbackWidth;
        const height = dimension?.height ?? 0;
        let nextTop = current.top;
        let nextLeft = current.left;
        switch (event.key) {
          case 'ArrowUp':
            nextTop -= delta;
            break;
          case 'ArrowDown':
            nextTop += delta;
            break;
          case 'ArrowLeft':
            nextLeft -= delta;
            break;
          case 'ArrowRight':
            nextLeft += delta;
            break;
        }
        const maxTop = height > 0 ? Math.max(0, CANVAS_HEIGHT - height) : CANVAS_HEIGHT;
        const clampedTop = Math.min(Math.max(nextTop, 0), maxTop);
        const maxLeft = width > 0 ? Math.max(0, CANVAS_WIDTH - width) : CANVAS_WIDTH;
        const clampedLeft = Math.min(Math.max(nextLeft, 0), maxLeft);
        return { ...current, top: clampedTop, left: clampedLeft };
      });
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editMode, selectedField, fieldDimensions, recipeFormat, updateFieldPosition, BASE_KEY_STEP]);

  const handleToggleEdit = () => {
    setEditMode((prev) => {
      if (prev) setSelectedField(null);
      return !prev;
    });
  };

  const handleResetPositions = () => {
    setFieldPositions({});
    setSelectedField(null);
    toast.success('Posiciones restablecidas');
  };

  const handleSavePositions = async () => {
    try {
      // Usar las claves correctas según el formato
      const allKeys = recipeFormat === 'beneficencia' 
        ? RECIPE_BENEFICENCIA_FIELD_KEYS 
        : recipeFormat === 'angeles'
        ? RECIPE_ANGELES_FIELD_KEYS
        : RECIPE_FIELD_KEYS;
      const defaultPositions: Record<string, FieldPosition> = recipeFormat === 'beneficencia'
        ? DEFAULT_BENEFICENCIA_POSITIONS
        : recipeFormat === 'angeles'
        ? DEFAULT_ANGELES_POSITIONS
        : DEFAULT_FIELD_POSITIONS;

      const mergedLayout = allKeys.reduce<Record<string, FieldPosition>>((acc, key) => {
        const base = defaultPositions[key];
        const override = fieldPositions[key];
        acc[key] = { ...base, ...(override ?? {}) };
        return acc;
      }, {});

      const hasChanges = allKeys.some((key) => {
        const next = mergedLayout[key];
        const prev = initialLayout[key];
        return next.top !== prev.top || next.left !== prev.left || (next.width ?? null) !== (prev.width ?? null);
      });
      if (!hasChanges) {
        toast.error('No hay cambios para guardar');
        return;
      }
      const positions = allKeys.map((campo) => ({
        campo,
        top: mergedLayout[campo].top,
        left: mergedLayout[campo].left,
        width: mergedLayout[campo].width,
      }));
      await pacientesService.updateRecetaLayout({ formato: recipeFormat, positions });
      toast.success('Posiciones guardadas exitosamente');
      setInitialLayout(mergedLayout);
    } catch (error) {
      console.error('Error al guardar posiciones:', error);
      toast.error('Error al guardar posiciones');
    }
  };

  const handlePrint = () => {
    setEditMode(false);
    setSelectedField(null);
    window.print();
  };

  const handleFormatChange = (format: RecipeFormat) => {
    setRecipeFormat(format);
    // Guardar preferencia en localStorage
    localStorage.setItem('recipeFormat', format);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!paciente) return null;

  const prescription = (state?.prescription ?? '').trim();
  // Overrides provenientes del bloque reimpreso
  const overrideDate = state?.date?.trim();
  const overrideAgeYears = state?.ageYears?.trim();
  const overrideAgeMonths = state?.ageMonths?.trim();
  const overrideAgeDays = state?.ageDays?.trim();
  const overrideWeight = state?.weight?.trim();
  const overrideHeight = state?.height?.trim();
  const overrideWeightPercentile = state?.weightPercentile?.trim();
  const overrideHeightPercentile = state?.heightPercentile?.trim();

  const patientName = `${paciente.nombre ?? ''} ${paciente.apellido1 ?? ''}`
    .trim()
    .toUpperCase();
  const weight = overrideWeight || (paciente?.peso != null ? String(paciente.peso) : '');
  const height = overrideHeight || (paciente?.talla != null ? String(paciente.talla) : '');
  const date = overrideDate || fechaMx;
  const ageYears = overrideAgeYears || String(edad.anios);
  const ageMonths = overrideAgeMonths || String(edad.meses);
  const ageDays = overrideAgeDays || String(edad.dias);
  const weightPercentileDisplay = overrideWeightPercentile || weightPercentile;
  const heightPercentileDisplay = overrideHeightPercentile || heightPercentile;
  const doctorName = doctorInfo?.nombre ?? 'Dr. José Luis Cadena Santillana';

  return (
    <div className="max-w-6xl mx-auto print:max-w-none print:mx-0">
      {/* Controles - no se imprimen */}
      <div className="flex flex-wrap gap-3 justify-between items-center mb-6 print:hidden">
        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              navigate('/medico-vfp/pacientes', {
                state: {
                  focusClave: paciente?.clave,
                  apellido1: paciente?.apellido1 ?? '',
                  apellido2: paciente?.apellido2 ?? '',
                  nombre: paciente?.nombre ?? '',
                },
              })
            }
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Volver
          </button>
          
          {/* Selector de formato */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Formato:</label>
            <select
              value={recipeFormat}
              onChange={(e) => handleFormatChange(e.target.value as RecipeFormat)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="beneficencia">Beneficencia Española</option>
              <option value="angeles">Hospital Ángeles</option>
            </select>
          </div>
          
          <button
            onClick={handleToggleEdit}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors border ${
              editMode
                ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                : 'text-blue-700 border-blue-200 hover:bg-blue-50'
            }`}
          >
            {editMode ? 'Salir modo edición' : 'Editar posiciones'}
          </button>
          {editMode && (
            <>
              <button
                onClick={handleSavePositions}
                className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                Guardar posiciones
              </button>
              <button
                onClick={handleResetPositions}
                className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                Restablecer posiciones
              </button>
            </>
          )}
        </div>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PrinterIcon className="w-5 h-5" />
          Imprimir Receta
        </button>
      </div>

      {/* Vista previa */}
      <div className="print:hidden">
        {editMode && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-4 py-3 text-sm space-y-1">
            <p className="font-semibold text-blue-900">Modo edición activo</p>
            <p>Selecciona un campo y usa las flechas para moverlo 1px.</p>
            <p>Mantén Shift para mover de 5px en 5px. Pulsa "Restablecer posiciones" para volver al diseño original.</p>
          </div>
        )}
        <div className="recipe-preview-shell">
          {recipeFormat === 'beneficencia' ? (
            <RecipeBeneficenciaCanvas
              patientName={patientName}
              ageYears={ageYears}
              ageMonths={ageMonths}
              ageDays={ageDays}
              weight={weight}
              weightPercentile={weightPercentileDisplay}
              height={height}
              heightPercentile={heightPercentileDisplay}
              prescription={prescription}
              doctorName={doctorName}
              consultDate={date}
              recetaToken={state?.recetaToken}
              editMode={editMode}
              selectedField={selectedField as any}
              onSelectField={setSelectedField as any}
              fieldPositions={fieldPositions as any}
              onFieldBoundsChange={handleFieldBoundsChange as any}
            />
          ) : (
            <RecipeAngelesCanvas
              patientName={patientName}
              ageYears={ageYears}
              ageMonths={ageMonths}
              ageDays={ageDays}
              weight={weight}
              weightPercentile={weightPercentileDisplay}
              height={height}
              heightPercentile={heightPercentileDisplay}
              prescription={prescription}
              consultDate={date}
              recetaToken={state?.recetaToken}
              editMode={editMode}
              selectedField={selectedField as any}
              onSelectField={setSelectedField as any}
              fieldPositions={fieldPositions as any}
              onFieldBoundsChange={handleFieldBoundsChange as any}
            />
          )}
        </div>
      </div>

      {/* Versión para impresión */}
      <div className="hidden print:block">
        {recipeFormat === 'beneficencia' ? (
          <RecipeBeneficenciaCanvas
            patientName={patientName}
            ageYears={ageYears}
            ageMonths={ageMonths}
            ageDays={ageDays}
            weight={weight}
            weightPercentile={weightPercentileDisplay}
            height={height}
            heightPercentile={heightPercentileDisplay}
            prescription={prescription}
            doctorName={doctorName}
            consultDate={date}
            recetaToken={state?.recetaToken}
            fieldPositions={fieldPositions as any}
            onFieldBoundsChange={handleFieldBoundsChange as any}
          />
        ) : (
          <RecipeAngelesCanvas
            patientName={patientName}
            ageYears={ageYears}
            ageMonths={ageMonths}
            ageDays={ageDays}
            weight={weight}
            weightPercentile={weightPercentileDisplay}
            height={height}
            heightPercentile={heightPercentileDisplay}
            prescription={prescription}
            consultDate={date}
            recetaToken={state?.recetaToken}
            fieldPositions={fieldPositions as any}
            onFieldBoundsChange={handleFieldBoundsChange as any}
          />
        )}
      </div>
    </div>
  );
}
